import type { Bbox, LocationNode } from '@/types/domain';

/** A level in the il / ilçe / mahalle hierarchy, shallowest first. */
export type LocationLevel = 'province' | 'district' | 'neighbourhood';

/** The shape reported to the caller: ids/names for whatever is selected, plus the deepest extent. */
export interface LocationSelection {
  countyCode?: number;
  city?: string;
  townId?: number;
  district?: string;
  neighbourhoodId?: number;
  neighbourhood?: string;
  /** Extent of the deepest level selected — what a map should frame. */
  bbox?: Bbox;
}

/** The actual nodes behind a selection, kept separately because a `LocationSelection` only ever carries one bbox. */
export interface SelectedNodes {
  province?: LocationNode;
  district?: LocationNode;
  neighbourhood?: LocationNode;
}

/**
 * Turns the nodes currently held at each level into the shape reported to
 * callers. The bbox always follows the deepest level that is actually
 * selected, falling back to a shallower level's extent once a deeper one is
 * cleared.
 */
export function toLocationSelection(nodes: SelectedNodes): LocationSelection {
  const bbox = nodes.neighbourhood?.bbox ?? nodes.district?.bbox ?? nodes.province?.bbox;
  return {
    countyCode: nodes.province?.id,
    city: nodes.province?.name,
    townId: nodes.district?.id,
    district: nodes.district?.name,
    neighbourhoodId: nodes.neighbourhood?.id,
    neighbourhood: nodes.neighbourhood?.name,
    bbox,
  };
}

/**
 * Whether anything at all is selected. Clearing the cascade back to its
 * placeholders reports an object with every field unset rather than no object,
 * so callers that store a selection need this to tell "anywhere" apart from a
 * real choice.
 */
export function isLocationSelected(selection: LocationSelection): boolean {
  return selection.countyCode != null;
}

/**
 * A stable string for one selection, so a caller can tell whether the place
 * being filtered on actually changed. Two selections of the same place compare
 * equal even though the cascade rebuilds the object on every edit, and an
 * emptied cascade keys the same as no selection at all so that clearing the
 * location is not reported twice.
 */
export function locationKey(selection?: LocationSelection): string {
  if (!selection || !isLocationSelected(selection)) return '';
  const { countyCode, townId, neighbourhoodId } = selection;
  return `${countyCode ?? ''}/${townId ?? ''}/${neighbourhoodId ?? ''}`;
}

/**
 * Applies a choice (or a clear, when `node` is undefined) at one level of the
 * hierarchy, given the nodes already held above it.
 *
 * Picking or clearing a level always wipes every level below it: a new
 * province can never leave a stale district or neighbourhood in place, and
 * clearing the district back to its placeholder clears the neighbourhood with
 * it. Anything above the level being changed is left untouched.
 */
export function selectAtLevel(
  current: SelectedNodes,
  level: LocationLevel,
  node: LocationNode | undefined
): LocationSelection {
  const next: SelectedNodes =
    level === 'province'
      ? { province: node }
      : level === 'district'
        ? { province: current.province, district: node }
        : { province: current.province, district: current.district, neighbourhood: node };

  return toLocationSelection(next);
}
